/** @format */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiFeedback, type ApiResponse } from '../app/useApiFeedback';
import { HasErrors } from '../common/Logic/ServerResponse';
import MessageResponse from '../common/feedback/messageResponse';
import { fetchApplicationDates } from '../features/home/homeApi';
import {
  getBannerImgSrcFromPayload,
  getCanonicalBannerUrlFromPayload,
} from '../features/home/bannerUrls';
import { updateHomeBannerImage } from '../features/admin/adminApi';
import '../features/home/home.css';

/** Shown when API returns no custom URL */
const FALLBACK_HOME_BANNER =
  'https://thuthukaprod.blob.core.windows.net/banners/2025.jpg';

function resolveBannerUrl(
  configured: string | null | undefined
): string {
  const t = (configured ?? '').trim();
  return t.length > 0 ? t : FALLBACK_HOME_BANNER;
}

export default function Banner() {
  const { feedback, handleApiResponse } = useApiFeedback();
  /** handleApiResponse is not memoized in useApiFeedback; keep a ref so loadCurrent stays stable. */
  const handleApiResponseRef = useRef(handleApiResponse);
  handleApiResponseRef.current = handleApiResponse;

  const [urlInput, setUrlInput] = useState('');
  const [previewUrl, setPreviewUrl] = useState(FALLBACK_HOME_BANNER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchApplicationDates();
      const err = HasErrors({ payload: resp });
      if (err === 2 || err === 3) {
        const d = resp?.data;
        setUrlInput(getCanonicalBannerUrlFromPayload(d));
        setPreviewUrl(
          getBannerImgSrcFromPayload(d) || resolveBannerUrl(null)
        );
      } else {
        handleApiResponseRef.current({ payload: resp }, false);
      }
    } catch (e: unknown) {
      handleApiResponseRef.current(
        {
          error: e,
          payload: (e as { response?: { data?: ApiResponse['payload'] } })
            ?.response?.data,
        },
        false
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  const handleSave = async () => {
    const trimmed = urlInput.trim();
    if (
      trimmed.length > 0 &&
      !/^https:\/\//i.test(trimmed)
    ) {
      handleApiResponse(
        {
          payload: {
            validation: { code: 400, messages: ['Use a full https:// image URL'] },
          },
        } as any,
        true
      );
      return;
    }

    setSaving(true);
    try {
      const resp = await updateHomeBannerImage({
        homeBannerImageUrl: trimmed.length > 0 ? trimmed : null,
      });
      handleApiResponse({ payload: resp } as any, true);
      const err = HasErrors({ payload: resp });
      if (err === 2 || err === 3) {
        const d = resp?.data;
        setUrlInput(getCanonicalBannerUrlFromPayload(d));
        setPreviewUrl(
          getBannerImgSrcFromPayload(d) || resolveBannerUrl(null)
        );
      }
    } catch (e: unknown) {
      handleApiResponse(
        {
          error: e,
          payload: (e as { response?: { data?: ApiResponse['payload'] } })
            ?.response?.data,
        },
        true
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography variant='h5' fontWeight={800} gutterBottom>
        Home page banner
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Set the hero image on the public home page (<strong>/</strong>). Use a
        direct HTTPS URL to an image already hosted (for example in Azure Blob
        Storage). Leave blank and save to use the default image.
      </Typography>

      <MessageResponse feedback={feedback} />

      <Alert severity='info' sx={{ mb: 2 }}>
        You must have at least one <strong>application dates</strong> record in
        the database (Application dates admin page). The banner is stored on
        that latest record.
      </Alert>

      {loading ? (
        <CircularProgress size={32} />
      ) : (
        <Stack spacing={3}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                Image URL
              </Typography>
              <TextField
                fullWidth
                size='small'
                placeholder={FALLBACK_HOME_BANNER}
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Stack direction='row' spacing={2} flexWrap='wrap'>
                <Button
                  variant='contained'
                  onClick={handleSave}
                  disabled={saving}>
                  {saving ? 'Saving…' : 'Save banner'}
                </Button>
                <Button variant='outlined' onClick={loadCurrent} disabled={saving}>
                  Reload from server
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card variant='outlined'>
            <CardContent>
              <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                Preview (same as home page)
              </Typography>
              <Box className='banner' sx={{ maxWidth: '100%' }}>
                <img
                  src={previewUrl}
                  alt='Banner preview'
                  className='banner_image'
                  onError={() =>
                    setPreviewUrl((prev) =>
                      prev === FALLBACK_HOME_BANNER ? prev : FALLBACK_HOME_BANNER
                    )
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
